'use client';

import { Popover } from '@base-ui/react/popover';
import { css } from '@repo/pandacss/styled-system/css';
import { useMemo } from 'react';
import { MdHelpOutline } from 'react-icons/md';
import { DisclosureTooltip } from '../DisclosureTooltip';
import { apyDriverTooltipClassName } from '../disclosureTooltipPopover';

interface ApyDriverProps {
  termLength: string;
  termLengthValue: string;
  tooltipClassName?: string;
  valueTooltip?: string;
  /** Paid summary table: omit redundant "APY" before the rate when the column header is APY. */
  suppressApyTermPrefix?: boolean;
  /** Icon size: 16 for product card, 14 for summary table. Defaults to 16. */
  iconSize?: 14 | 16;
}

const ApyDriver = ({
  termLength,
  termLengthValue,
  tooltipClassName = apyDriverTooltipClassName,
  valueTooltip,
  suppressApyTermPrefix = false,
  iconSize = 16,
}: ApyDriverProps): React.JSX.Element => {
  const handler = useMemo(() => Popover.createHandle<unknown>(), []);

  const showValueOnly = suppressApyTermPrefix && termLength === 'APY';
  const driverValue = showValueOnly ? termLengthValue : `${termLength} ${termLengthValue}`;

  return (
    <div
      className={css({
        display: 'inline',
      })}
    >
      <span
        className={css({
          whiteSpace: 'normal',
          display: 'inline',
          verticalAlign: 'middle',
        })}
      >
        {driverValue}
      </span>
      <span
        className={css({
          display: 'inline',
          whiteSpace: 'nowrap',
        })}
      >
        &nbsp;
        {valueTooltip && (
          <>
            <Popover.Trigger
              handle={handler}
              className={css({
                borderTopStyle: 'none',
                borderRightStyle: 'none',
                borderBottomStyle: 'none',
                borderLeftStyle: 'none',
                backgroundColor: 'transparent',
                paddingTop: '0',
                paddingRight: '0',
                paddingBottom: '0',
                paddingLeft: '0',
                cursor: 'pointer',
                display: 'inline-flex',
                color: 'neutral.black',
                verticalAlign: 'middle',
              })}
              aria-label={`${termLength} info`}
            >
              <MdHelpOutline size={iconSize} />
            </Popover.Trigger>
            <DisclosureTooltip handler={handler} tooltipClassName={tooltipClassName}>
              <span
                className={css({
                  textStyle: 'textSmall',
                  lineHeight: 'xl',
                })}
              >
                {valueTooltip}
              </span>
            </DisclosureTooltip>
          </>
        )}
      </span>
    </div>
  );
};

export default ApyDriver;
